/**
 * Arrears movement chart for Cached Performance (CR-4751).
 *
 * Shows arrears balance over time as a line, with period-by-period movements
 * (new arrears added vs resolved) as overlaid bars on a secondary axis. This
 * reads more clearly than a traditional waterfall staircase when there are
 * many periods.
 *
 * Consumes arrears_opening_value, arrears_new_value, arrears_resolved_value,
 * arrears_closing_value from the trends payload.
 */
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts'
import { Info } from 'lucide-react'

import { ChartWrapper } from '../ChartWrapper'
import { useFormatters } from '../../../contexts/PreferencesContext'
import type { MetricType, TrendPoint } from '../../../lib/api/dashboard-snapshots'
import { asNumber, formatChartLabel, pointDate } from './helpers'

interface CachedArrearsWaterfallProps {
  trends: Record<MetricType, TrendPoint[]>
  period: 'daily' | 'weekly' | 'monthly' | 'quarterly'
}

interface WaterfallRow {
  month: string
  opening: number
  newValue: number
  resolved: number
  closing: number
}

function buildRows(
  trends: Record<MetricType, TrendPoint[]>,
  period: 'daily' | 'weekly' | 'monthly' | 'quarterly',
): WaterfallRow[] {
  const byDate = new Map<string, WaterfallRow>()
  const push = (points: TrendPoint[], key: keyof Omit<WaterfallRow, 'month'>) => {
    points.forEach((p) => {
      // CR-4751: keyed and ordered by the period measured, not the capture date.
      const date = pointDate(p)
      const existing = byDate.get(date) ?? {
        month: formatChartLabel(date, period), opening: 0, newValue: 0, resolved: 0, closing: 0,
      }
      existing[key] = asNumber(p.metric_value)
      byDate.set(date, existing)
    })
  }
  push(trends.arrears_opening_value ?? [], 'opening')
  push(trends.arrears_new_value ?? [], 'newValue')
  push(trends.arrears_resolved_value ?? [], 'resolved')
  push(trends.arrears_closing_value ?? [], 'closing')
  return Array.from(byDate.keys()).sort().map((date) => byDate.get(date) as WaterfallRow)
}

export function CachedArrearsWaterfall({ trends, period }: CachedArrearsWaterfallProps) {
  const { preferences, formatCurrency, formatCompactCurrency } = useFormatters()
  const rows = buildRows(trends, period)

  return (
    <ChartWrapper
      title={`Arrears Balance and Movement (${preferences.currency})`}
      data={rows}
      exportFilename="cached-arrears-movement"
      legend={[
        { color: '#94a3b8', label: 'Opening', dataKey: 'opening' },
        { color: '#2563eb', label: 'Closing', dataKey: 'closing' },
        { color: '#ef4444', label: 'New (added)', dataKey: 'newValue' },
        { color: '#10b981', label: 'Resolved', dataKey: 'resolved' },
      ]}
    >
      {rows.length === 0 ? (
        <div className="h-[240px] flex flex-col items-center justify-center text-muted-foreground gap-2">
          <Info className="w-6 h-6" />
          <p className="text-sm">No arrears-movement snapshots yet.</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={50} />
            <YAxis
              yAxisId="balance" tick={{ fontSize: 11 }} tickFormatter={(v: number) => formatCompactCurrency(v)}
              label={{ value: 'Balance', angle: -90, position: 'insideLeft', fontSize: 10, fill: '#6b7280' }}
            />
            <YAxis
              yAxisId="movement" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v: number) => formatCompactCurrency(v)}
              label={{ value: 'Movement', angle: 90, position: 'insideRight', fontSize: 10, fill: '#6b7280' }}
            />
            <Tooltip
              cursor={{ fill: 'rgba(59, 130, 246, 0.08)' }}
              formatter={(v: number) => formatCurrency(v)}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="movement" dataKey="newValue" fill="#ef4444" name="New (added)" />
            <Bar yAxisId="movement" dataKey="resolved" fill="#10b981" name="Resolved" />
            <Line yAxisId="balance" type="monotone" dataKey="opening" stroke="#94a3b8" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} name="Opening" />
            <Line yAxisId="balance" type="monotone" dataKey="closing" stroke="#2563eb" strokeWidth={3} dot={{ r: 4, fill: '#2563eb', stroke: '#fff', strokeWidth: 2 }} name="Closing" />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </ChartWrapper>
  )
}
